import React from 'react';
import { Menu, theme, type MenuProps } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  DashboardOutlined, 
  TeamOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { StyledSider, Logo } from '../../styles/common';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';

const StyledMenu = styled(Menu)`
  &.ant-menu {
    /* Style for the selected menu item */
    .ant-menu-item-selected {
      font-weight: bold;
      background-color: ${(props) => (props.theme.isDarkMode ? '#333333' : '#f0f0f0')} !important;
      color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;

      .anticon {
        color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;
      }
    }

    /* Hover effect for menu items */
    .ant-menu-item:hover {
      background-color: ${(props) => (props.theme.isDarkMode ? '#444444' : '#e0e0e0')} !important;
      color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;
    }

    /* Make sidebar menu links fill the menu item area */
    .sidebar-menu-link {
      display: block;
      width: 100%;
      color: inherit;
      text-decoration: none;
    }
  }
`;

const crmMenuItems = [
  {
    key: 'crm',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
    path: '/crm'
  },
  {
    key: 'crm/customers',
    icon: <TeamOutlined />,
    label: 'Customers',
    path: '/crm/customers'
  },
  {
    key: 'crm/requests',
    icon: <FileTextOutlined />,
    label: 'Requests',
    path: '/crm/requests'
  }
];

interface CrmSidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  isMobile?: boolean;
}

const CrmSidebar: React.FC<CrmSidebarProps> = ({ collapsed, onCollapse, isMobile = false }) => {
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const { permissions } = useAuth();

  const getSelectedKeys = () => {
    const path = location.pathname.substring(1); // Remove leading slash
    
    // For request view pages, select the requests menu item
    if (path.startsWith('crm/requests/')) {
      return ['crm/requests'];
    }
    
    // For exact matches
    const menuItem = crmMenuItems.find(item => item.path === `/${path}`);
    if (menuItem) {
      return [menuItem.key];
    }
    
    // Default to dashboard
    return ['crm'];
  };

  const showFullText = isMobile ? true : !collapsed;
  const logoText = showFullText ? 'CRM System' : 'CRM';

  return (
    <StyledSider
      collapsible
      collapsed={isMobile ? false : collapsed}
      onCollapse={onCollapse}
      width={250}
      collapsedWidth={isMobile ? 0 : 80}
      trigger={null}
      style={isMobile ? {
        position: 'fixed',
        height: '100vh',
        left: 0,
        top: 0,
        zIndex: 1001,
        transform: collapsed ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.3s ease',
      } : undefined}
    >
      <Logo>{logoText}</Logo>
      <StyledMenu
        mode="inline"
        defaultSelectedKeys={['crm']}
        selectedKeys={getSelectedKeys()}
        style={{ borderRight: 0, flex: 1 }}
        items={crmMenuItems
          .filter((item) => hasPermission(PERMISSIONS.CRM_MANAGEMENT, 'view', permissions || undefined))
          .map((item) => ({
            key: item.key,
            icon: item.icon,
            label: (
              <Link to={item.path} className="sidebar-menu-link">
                {item.label}
              </Link>
            ),
          }))}
      />
    </StyledSider>
  );
};

export default CrmSidebar;
